<template>
  <div>
    <BaseSelect
      :label="label"
      :hasIcon="hasIcon"
      :disabled="disabled"
      @click="toggleSelect()"
      class="payment-select"
    >
      <component :is="loadIconPath" v-if="iconType" :iconColor="iconColor" />
      <DropdownSelect
        :name="name"
        :isOpen="isOpen"
        :hasOneItemOnly="listHasOneItem"
        @onClickOutside="handleClickOutside"
      >
        <template #selectedItem>
          <PaymentSelectListItem
            v-if="selectedSingle"
            class="payment-select__item---selected"
            @keydown.enter="toggleSelect(false)"
            @keydown.space="toggleSelect(false)"
            tabindex="0"
            :item="selectedSingle"
          />
        </template>
        <template #dropdownOptions>
          <PaymentSelectList
            id="paymentList"
            :items="items"
            :counterIndex="counterIndex"
            :isOpen="isOpen"
            @selectElement="selectEl"
            @selectElOnClick="selectElOnClick"
            @moveToNextElement="goToNextEl"
            @moveToPreviousElement="goToPreviousEl"
            @close="toggleSelect(false)"
            @click="toggleSelect()"
          />
        </template>
      </DropdownSelect>
    </BaseSelect>
    <PaymentDetailCard v-if="!hideDescription"
      />
  </div>
</template>

<script setup>
import { watch, computed, defineAsyncComponent } from "vue";

import DropdownSelect from "@/components/ui/selects/DropdownSelect.vue";
import useSelectList from "@/composables/ui/useSelectList.js";

import BaseSelect from "@/components/ui/inputs/BaseSelect.vue";
import PaymentSelectList from "@/components/ui/selects/PaymentSelectList.vue";
import PaymentSelectListItem from "@/components/ui/selects/PaymentSelectListItem.vue";
import PaymentDetailCard from "@/components/ui/cards/PaymentDetailCard.vue";

const props = defineProps({
  disabled: { type: Boolean, default: false },
  defaultItem: { type: String, default: "" },
  items: { type: Array, required: true },
  name: { type: String, default: "default-select" },
  label: { type: String, default: "Default label" },
  placeholder: { type: String, default: "Default placeholder" },
  iconType: { type: String, required: false, default: "" },
  iconColor: { type: String, required: false, default: "" },
  hideDescription: {type: Boolean, required: true, default: false}
});

const { id } = props;

const emit = defineEmits(["dropdownValueUpdate"]);
const hasIcon = !!props.iconType;

const {
  isOpen,
  counterIndex,
  goToNextEl,
  selectEl,
  selectElOnClick,
  goToPreviousEl,
  selectedSingle,
  toggleSelect,
  updateCurrentList,
  listHasOneItem,
  handleClickOutside,
} = useSelectList({
  items: props.items,
  defaultItem: props.defaultItem,
});

const loadIconPath = computed(() => {
  return defineAsyncComponent(() =>
    import(/* @vite-ignore */ `../../../components/icons/${props.iconType}.vue`)
  );
});

watch(
  selectedSingle,
  () => {
    emit("dropdownValueUpdate", {
      [props.name]: selectedSingle.value,
    });
  },
  { deep: true }
);

watch(
  () => props.items,
  (newItems, prevItems) => {
    updateCurrentList(newItems, props.defaultItem);
  },
  { deep: true }
);

watch(
  () => props.defaultItem,
  () => {
    updateCurrentList(props.items, props.defaultItem);
  },
  { deep: true }
);
</script>

<style lang="scss">
.payment-select {
  margin-bottom: 0rem;

  &__selected-item {
    .payment-logo {
      max-width: 60px;
    }
  }

  .select-list__menu{
    max-width: 100%;
  }

  .dropdown-select {
    height: 100%;
    min-height: 50px;
    border-radius: $border-radius-dropdown-with-card;
  }
}
</style>
